import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Sparkles, CheckSquare, Gift, ExternalLink, Loader2 } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';

interface AIGiftSuggestionsProps {
  onNavigate: (screen: string, data?: { giftGroupId?: string; tab?: string }) => void;
  giftGroupId: string | null;
  embedded?: boolean;
  onSuggestionsAdded?: () => void;
}

const descriptorTags = [
  'Workmate', 'Boss', 'Customer', 'Client', 'Alumni',
  'Runner', 'Hiker', 'Boater', 'Camper', 'Gamer',
  'Foodie', 'Rocker', 'Classical', 'Artist', 'Reader',
  'Tech Enthusiast', 'Fitness', 'Yoga', 'Golfer', 'Traveler'
];

export function AIGiftSuggestions({ onNavigate, giftGroupId, embedded = false, onSuggestionsAdded }: AIGiftSuggestionsProps) {
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedGifts, setSelectedGifts] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);

  // Mark AI suggestions as viewed when component mounts
  useEffect(() => {
    const markAiViewed = async () => {
      if (!giftGroupId) return;
      
      try {
        await api.markAIViewed(giftGroupId);
        console.log('Marked AI suggestions as viewed');
      } catch (error) {
        console.error('Failed to mark AI viewed:', error);
        // Don't show error toast - this is a background action
      }
    };
    
    markAiViewed();
  }, [giftGroupId]);

  const toggleTag = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const toggleGift = (id: number) => {
    setSelectedGifts(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedGifts([]);
    } else {
      setSelectedGifts(aiSuggestions.map((_, index) => index));
    }
    setSelectAll(!selectAll);
  };

  // Get AI suggestions when tags change
  useEffect(() => {
    if (selectedTags.length > 0 && giftGroupId) {
      const fetchSuggestions = async () => {
        try {
          setLoading(true);
          const response = await api.getAISuggestions(giftGroupId, selectedTags);
          setAiSuggestions(response.suggestions || []);
        } catch (error) {
          console.error('Failed to get AI suggestions:', error);
          toast.error('Failed to get AI suggestions');
        } finally {
          setLoading(false);
        }
      };
      fetchSuggestions();
    } else {
      setAiSuggestions([]);
    }
  }, [selectedTags, giftGroupId]);

  const handleAddSuggestions = async () => {
    if (!giftGroupId) {
      toast.error('No gift group selected');
      return;
    }

    try {
      setAdding(true);
      const selectedSuggestions = aiSuggestions.filter((_, index) => selectedGifts.includes(index));
      
      // Add each selected suggestion to the gift group
      for (const suggestion of selectedSuggestions) {
        await api.addGiftSuggestion(giftGroupId, {
          url: suggestion.url,
          name: suggestion.name,
          description: suggestion.description,
          price: suggestion.price,
          imageUrl: suggestion.imageUrl,
          source: 'ai'
        });
      }

      toast.success(`Added ${selectedSuggestions.length} suggestion${selectedSuggestions.length !== 1 ? 's' : ''}`);
      
      // If embedded, call the callback to switch back to rankings view
      if (embedded && onSuggestionsAdded) {
        onSuggestionsAdded();
      } else {
        // Otherwise navigate to gift-suggestions page (standalone mode)
        onNavigate('gift-suggestions', { giftGroupId });
      }
    } catch (error) {
      console.error('Failed to add suggestions:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error('Failed to add suggestions', {
        description: errorMessage.includes('no such table') 
          ? 'Database tables not set up. Please run the migration first.'
          : errorMessage
      });
    } finally {
      setAdding(false);
    }
  };

  const handleAddOwn = () => {
    if (giftGroupId) {
      onNavigate('gift-suggestions', { giftGroupId });
    }
  };

  if (!giftGroupId) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="p-6 text-center">
          <h2 className="text-xl mb-2">No Gift Group Selected</h2>
          <p className="text-muted-foreground mb-4">Please select a gift group first</p>
          <Button onClick={() => onNavigate('dashboard', { tab: 'todo' })}>Go to Dashboard</Button>
        </Card>
      </div>
    );
  }

  const content = (
    <>
      {!embedded && (
        <Card className="p-6 border-2 border-primary/20 mb-6 shadow-lg">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Sparkles className="w-6 h-6 text-purple-600" />
            </div>
            <h1 className="text-primary">AI Gift Suggestions</h1>
          </div>
          <p className="text-muted-foreground">Help us suggest perfect gifts using AI</p>
        </Card>
      )}

        {/* Descriptor Tags */}
        <Card className="p-6 border-2 border-primary/20 mb-6 shadow-lg">
          <h2 className="text-xl mb-4 text-primary">Describe the Giftee</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Select descriptive words that match the recipient. Gift suggestions will update automatically.
          </p>
          
          <div className="flex flex-wrap gap-2">
            {descriptorTags.map((tag) => (
              <Badge
                key={tag}
                variant={selectedTags.includes(tag) ? 'default' : 'outline'}
                className="cursor-pointer text-sm py-2 px-4 transition-colors hover:bg-primary/20"
                onClick={() => toggleTag(tag)}
              >
                {tag}
              </Badge>
            ))}
          </div>

          {selectedTags.length > 0 && (
            <div className="mt-4 p-3 bg-blue-50 border-2 border-blue-200 rounded-lg">
              <div className="text-sm">
                <strong>Selected:</strong> {selectedTags.join(', ')}
              </div>
            </div>
          )}
        </Card>

        {/* AI Generated Suggestions */}
        <Card className="border-2 border-primary/20 shadow-lg">
          <div className="p-6 border-b-2 border-primary/20 flex items-center justify-between">
            <div>
              <h2 className="text-xl mb-1 text-primary">AI-Powered Gift Suggestions</h2>
              <p className="text-sm text-muted-foreground">
                {selectedTags.length > 0 
                  ? `Based on: ${selectedTags.join(', ')}`
                  : 'Select descriptors above to see personalized suggestions'
                }
              </p>
            </div>
            {aiSuggestions.length > 0 && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="select-all"
                  checked={selectAll}
                  onCheckedChange={handleSelectAll}
                />
                <Label htmlFor="select-all" className="cursor-pointer">
                  Select All
                </Label>
              </div>
            )}
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-12 h-12 mx-auto mb-4 text-primary animate-spin" />
              <p className="text-muted-foreground">Getting AI suggestions...</p>
            </div>
          ) : aiSuggestions.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>Select descriptor tags above to see AI-generated gift suggestions</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Gift Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {aiSuggestions.map((gift, index) => (
                  <TableRow key={index} className="hover:bg-muted/50">
                    <TableCell>
                      <Checkbox
                        checked={selectedGifts.includes(index)}
                        onCheckedChange={() => toggleGift(index)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Gift className="w-4 h-4 text-muted-foreground" />
                        <strong>{gift.name}</strong>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {gift.description}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">${gift.price.toFixed(2)}</Badge>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" asChild>
                        <a href={gift.url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        {!embedded && (
          <div className="mt-6 space-y-4">
            <div className="flex gap-4">
              <Button
                onClick={handleAddSuggestions}
                size="lg"
                className="flex-1 bg-primary hover:bg-primary/90"
                disabled={selectedGifts.length === 0 || adding}
              >
                {adding ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <CheckSquare className="w-5 h-5 mr-2" />
                    Add {selectedGifts.length} Selected Suggestion{selectedGifts.length !== 1 ? 's' : ''} to List
                  </>
                )}
              </Button>
              <Button
                onClick={handleAddOwn}
                size="lg"
                variant="outline"
                className="flex-1 border-primary/30"
              >
                Skip & Add My Own Suggestions
              </Button>
            </div>

            <div className="flex justify-between items-center flex-wrap gap-4">
              <div className="flex gap-2 flex-wrap">
                <Button 
                  variant="outline" 
                  onClick={() => onNavigate('gift-suggestions', { giftGroupId })}
                >
                  Add Manual Suggestions
                </Button>
                <Button 
                  className="bg-primary hover:bg-primary/90"
                  onClick={() => onNavigate('gift-group-overview', { giftGroupId })}
                >
                  Go to Rankings
                </Button>
              </div>
            </div>
          </div>
        )}

        {embedded && (
          <div className="mt-6">
            <Button
              onClick={handleAddSuggestions}
              size="lg"
              className="w-full bg-primary hover:bg-primary/90"
              disabled={selectedGifts.length === 0 || adding}
            >
              {adding ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <CheckSquare className="w-5 h-5 mr-2" />
                  Add {selectedGifts.length} Selected Suggestion{selectedGifts.length !== 1 ? 's' : ''} to List
                </>
              )}
            </Button>
          </div>
        )}
      </>
  );
  
  if (embedded) {
    return content;
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-6xl mx-auto">
        {content}
      </div>
    </div>
  );
}